import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../../utils/axiosInstance';
import {
  getServiceConfigsByStoreIdUrl,
  serviceConfigsUrl,
  servicesUrl,
  serviceTypesUrl,
} from '../../../utils/urls';

export default function AddStoreService() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const storeName = location.state?.name || '—';

  const [services, setServices] = useState([]);
  const [configuredServiceIds, setConfiguredServiceIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axiosInstance.get(servicesUrl),
      axiosInstance.get(serviceTypesUrl),
      axiosInstance.get(getServiceConfigsByStoreIdUrl(storeId)),
    ])
      .then(([svcRes, typeRes, cfgRes]) => {
        const cfgData = Array.isArray(cfgRes.data) ? cfgRes.data : [];
        const svcIdSet = new Set(cfgData.map(c => c.service.id));
        setConfiguredServiceIds(svcIdSet);

        const svcData = Array.isArray(svcRes.data) ? svcRes.data : svcRes.data.data || [];
        const typeData = Array.isArray(typeRes.data) ? typeRes.data : [];
        setServices(
          svcData.map(svc => ({
            id: svc.id,
            name: svc.name.trim(),
            types: typeData
              .filter(t => t.serviceId === svc.id)
              .map(t => ({ id: t.id, name: t.name.trim() })),
          }))
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [storeId]);

  const formik = useFormik({
    initialValues: { serviceIds: [], types: {} },
    validationSchema: Yup.object().shape({
      serviceIds: Yup.array().min(1, 'Select at least one service'),
      types: Yup.object().test(
        'price-required',
        'Each selected type needs a price',
        values => {
          const selections = Object.entries(values)
            .filter(([key, val]) => !key.endsWith('_price') && val)
            .map(([key]) => key);
          return (
            selections.length > 0 &&
            selections.every(key => {
              const price = values[`${key}_price`];
              return (
                price !== undefined &&
                price !== '' &&
                !isNaN(price) &&
                Number(price) >= 0
              );
            })
          );
        }
      ),
    }),
    onSubmit: async values => {
      setSaving(true);
      try {
        const calls = [];
        values.serviceIds.forEach(svcId => {
          const svc = services.find(s => s.id === svcId);
          svc.types.forEach(type => {
            const key = `${svcId}_${type.id}`;
            if (values.types[key]) {
              const priceKey = `${key}_price`;
              calls.push(
                axiosInstance.post(serviceConfigsUrl, {
                  name: `${svc.name} – ${type.name}`,
                  description: `Config for ${svc.name}/${type.name}`,
                  unitType: 'unit',
                  price: Number(values.types[priceKey]),
                  isDefault: false,
                  serviceId: svcId,
                  serviceTypeId: type.id,
                  storeId,
                })
              );
            }
          });
        });
        await Promise.all(calls);
        navigate(-1);
      } catch (err) {
        console.error('Save failed:', err);
      } finally {
        setSaving(false);
      }
    },
  });

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const availableServices = services.filter(
    svc =>
      !formik.values.serviceIds.includes(svc.id) &&
      !configuredServiceIds.has(svc.id)
  );
  const selectedDetails = formik.values.serviceIds
    .map(id => services.find(s => s.id === id))
    .filter(Boolean);

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Typography variant="h5">Add Services for {storeName}</Typography>
      <form onSubmit={formik.handleSubmit}>
        <FormControl fullWidth sx={{ mb: 2 }} error={!!formik.errors.serviceIds}>
          <InputLabel id="service-select-label">Select Services</InputLabel>
          <Select
            labelId="service-select-label"
            multiple
            name="serviceIds"
            value={formik.values.serviceIds}
            onChange={e => formik.setFieldValue('serviceIds', e.target.value)}
            input={<OutlinedInput label="Select Services" />}
            renderValue={vals => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {vals.map(id => (
                  <Chip key={id} label={services.find(s => s.id === id)?.name} />
                ))}
              </Box>
            )}
          >
            {availableServices.map(svc => (
              <MenuItem key={svc.id} value={svc.id}>
                {svc.name}
              </MenuItem>
            ))}
          </Select>
          {formik.errors.serviceIds && (
            <Typography variant="caption" color="error">
              {formik.errors.serviceIds}
            </Typography>
          )}
        </FormControl>

        {selectedDetails.map(service => (
          <Card key={service.id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {service.name}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {service.types.map(type => {
                  const key = `${service.id}_${type.id}`;
                  const checked = !!formik.values.types[key];
                  return (
                    <Box key={type.id} sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'grey.300', borderRadius: 1, p: 1, gap: 1 }}>
                      <Checkbox
                        checked={checked}
                        onChange={() => {
                          formik.setFieldValue(`types.${key}`, !checked);
                          if (checked) formik.setFieldValue(`types.${key}_price`, '');
                        }}
                      />
                      <Typography sx={{ flexGrow: 1 }}>{type.name}</Typography>
                      {checked && (
                        <TextField
                          size="small"
                          type="number"
                          name={`types.${key}_price`}
                          value={formik.values.types[`${key}_price`] || ''}
                          onChange={e => formik.setFieldValue(`types.${key}_price`, e.target.value)}
                          inputProps={{ min: 0 }}
                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                          sx={{ width: 80 }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving || !formik.isValid}>
            {saving ? <CircularProgress size={20} /> : 'Save Services'}
          </Button>
        </Box>
      </form>
    </Stack>
  );
}
